import {BaseElementAsync, BASIS, MsgLabel, OSkinableInit} from "back/commons/basis";
import {AppFrameDeskFeat, IApp, IAppCtx} from "back/core/appFrame";
import {AppHeader} from "back/core/widgets/appHeader";
import {OWorkbenchInit, Workbench} from "back/core/widgets/workbench";
import {DeleteSrc, ExportScar, ExportScwsp, FocusItemOrSpace, ImportFiles, ImportScar, OpenFastFindItem, OpenSearchItems, OpenWspTrash, RenameSrc, SetPermsSpace, ShortDescCopy, ShowNetItems} from "back/wsp/actions/srcActions";
import {JWspAppDef, JWspDocAppDef} from "back/wsp/plugins/wspsPlg";
import {BookmarksArea, ExplorerArea, GeneratorTabsArea, NetItemsArea, SearchesArea, TasksArea, TocArea} from "back/wsp/areas/wspWorkbench";
import {SpaceTreeCollapseAllAction, SpaceTreeCopyTo, SpaceTreeCreateItem, SpaceTreeCreateSpace, SpaceTreeDuplicate, SpaceTreeFocusItem, SpaceTreeMoveTo, SpaceTreePaste, SpaceTreePasteMove, SpaceTreeRename} from "back/wsp/views/spaceTree";
import "back/wsp/views/views_Perms";
import {Action, ActionMenu, ActionSeparator, ActionSignboardWrapper, IAccelKeyMgrPointer, IAction, IActionToggle} from "lib/commons/actions";
import {IInfo, IInfoConsumer, InfoBrokerBasic} from "lib/commons/infos";
import {IReg, IRegPointer, REG} from 'lib/commons/registry';
import {IView, VIEWS} from "lib/commons/views";
import {JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IChainEnv} from "lib/wsp/chain";
import {InfoCurrentItem, InfoFocusItem, InfoReqCurrentItem, IShortDescCtx, ITEM} from "lib/wsp/item";
import {JSrcFields, SRC, srcRef} from "lib/wsp/src";
import {IWspEnv, IWspUiEnv} from "lib/wsp/wsp";
import {ILastDatasBuilder, JLastDatas, LASTDATAS} from "lib/commons/lastDatas";
import {IItemViewer} from "back/wsp/views/itemMain";
import {ItemViewerMulti} from "back/wsp/views/itemViewerMulti";
import {
	ELong2DateStyles,
	ESearchExportColumnStatus,
	ISearchFunc,
	ISearchPersist,
	ISearchStore,
	SearchExportColumn,
	SearchFuncAccountProp,
	SearchFuncLong2Date,
	SearchFuncProps,
	SearchFuncResponsibles,
	SearchFuncTranslateStr,
	SearchFuncUri2LeafName,
	SearchFuncUri2Space
} from "lib/wsp/search";
import {Searches} from "back/wsp/views/searches";
import {GridColDef} from "back/commons/widgets/grid-core";
import {
	CellBuilderItModel,
	CellBuilderItModelFlagIcon,
	CellBuilderLc,
	CellBuilderLcFlag,
	CellBuilderResp,
	CellBuilderResps,
	CellBuilderSpace,
	CellBuilderSrcIconCode,
	CellBuilderTkDeadline,
	CellBuilderTkScheduledD
} from "back/wsp/widgets/srcGridColumns";
import {CellBuilderDate, CellBuilderString} from "back/commons/widgets/grid-libs";
import {IArea} from "lib/commons/areas";
import {CellBuilderAccount} from "back/core/widgets/usersGrid";
import {ExportRes, ImportFolder, ImportRes, ItemsSetResonsabilities} from "back/wsp/actions/itemActions";
import {RibbonLc, RibbonResp, RibbonTasks} from "back/wsp/widgets/ribbon/ribbon";
import {ItemListTransition} from "back/wsp/actions/lcTaskActions";
import {registerWspProtocolsActions} from "back/wsp/actions/scProtocolActions";
import "back/wsp/apps/apps_Perms";
import {WspBaseApp} from "back/wsp/apps/wspBaseApp";
import {IWedSearchCoordinatorPointer, WedSearchCoordinator} from "back/edit/wed/features/searchBar";
import {EItemTypeFamily, ETaskFeatures, ItemTypeTask, LC_DEFAULT_NAME} from "lib/wsp/wspMetaUi";
import {EGitState, JGitSrcFields} from "lib/wsp/git";
import {HistoEditPointsMgr, IHistoEditEnv} from "lib/edit/histoEditPoints";
import {ActionBtn} from "back/commons/widgets/buttons";
import {ScanFsUpdatesAction} from "back/wsp/actions/wspActions";

export interface OWspAppInit extends OSkinableInit, IAppCtx<IChainEnv> {
	reg?: IReg<IChainEnv>
	itemViewer?: IItemViewer
	appDef: JWspAppDef
}

/** App d'accès complet à un atelier. */
export interface WspApp extends BaseElementAsync, IView {
	initialize(init: OWspAppInit): this;
}

export class WspApp extends WspBaseApp implements IApp<IWspEnv>, IInfoConsumer, IAccelKeyMgrPointer<IRegPointer<IWspUiEnv>>, ILastDatasBuilder, IShortDescCtx {


	appDef: JWspAppDef;

	appHeader: AppHeader<WspApp>;

	workbench: Workbench<IApp<IWspEnv>>;

	itemViewer: IItemViewer;

	protected _config: OWspAppInit;

	/** mémoire des derniers labels utilisés pour le label de la view. */
	protected _lastSrcLabel: string;

	protected _lastWspLabel: string;

	/** Api IShortDesc */
	shortDescs: JSrcFields[] = [];

	/** Api IShortDesc */
	get emitter() {return this}

	protected async _initialize(init: OWspAppInit): Promise<void> {
		this._config = init;
		this.appDef = init.appDef;
		this.universe = init.reg.env.universe;

		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.wsp = this.universe.wspServer.wspsLive.findWsp(this.appDef.wsp, true);
		return this._initApp(init);
	}

	/** Init ou RE-init de l'app. */
	protected async _initApp(init: OWspAppInit) {
		this.infoBroker = new InfoBrokerBasic();
		this.infoBroker.addConsumer(this);
		this.workbench = null;
		this.itemViewer = null;
		const sr = this.shadowRoot;
		const pendingUi = new MsgLabel();

		if (!await this._initWsp(init, pendingUi)) return;

		if (!await this._initReg(init, "wspApp", "ui.wspApp", pendingUi)) return;
		const reg = this.reg;
		(this.reg.env as IWedSearchCoordinatorPointer).wedSearchCoord = new WedSearchCoordinator();

		BASIS.clearContent(sr);

		//Construction du workbench
		this.workbench = sr.appendChild(<Workbench class={reg.getUserData("showFlapTabs", true) ? undefined : "hideFlapTabs"} î={{
			toolbarsPrefix: "wspApp:tools:",
			reg,
			ctx: this,
			skinOver: "wsp-app/c-workbench",
			lastDatasKey: "wb",
			lastDatas: init.lastDatas && init.lastDatas["wb"],
			//par défaut on masque les views en bas et à droite.
			lb: {selTab: ""},
			rt: {selTab: ""},
			rb: {selTab: ""},
			bl: {selTab: ""},
			br: {selTab: ""}
		} as OWorkbenchInit}/>) as Workbench<IApp<IWspEnv>>;

		if (reg.env.universe.config.backEnd === "fs") {
			const explo = this.workbench.getTab("explorer");
			if (explo) {
				const btn = ActionBtn.buildButton(new ScanFsUpdatesAction(), this, "bar", explo);
				btn.className = "label"; //pour êter masqué si volet clos.
				explo.appendChild(btn);
			}
		}

		//AppHeader (après le workbench car on le référence dans le appHeader)
		this._initAppHeader(init, [this.workbench, ...this.workbench.tools], "actions:wspApp:main", "actions:wspApp:more");
		this.workbench.appendChild(this.appHeader);

		//
		this._refreshTitle(null);
		this._refreshMigration();

		//Construction de la mainView dans le workbench
		const main = this.workbench.appendChild(<main/>);
		this.itemViewer = init.itemViewer || new ItemViewerMulti();
		if (this.wsp.hasFeature('trash')) this.itemViewer.fallbackOpenSrcRef = this.fallbackOpenSrcRef;
		await this.itemViewer.initViewer(reg, main, init.lastDatas, this.appDef.srcRef);
		if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
		init.lastDatas = undefined;

		const injectLoads = reg.getList<(app: WspApp) => void | Promise<void>>("wspApp:load");
		if (injectLoads) for (const lstn of injectLoads) {
			const result = lstn(this);
			if (result instanceof Promise) {
				await result;
				if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
			}
		}
	}

	reloadApp() {
		this.onViewHidden(true)
		this.reinitializeStarted(this._initApp(this._config));
	}

	onViewShown() {
		if (this.initialized) {
			if (this.workbench) VIEWS.onContainerShown(this.workbench);
			if (this.itemViewer) this.itemViewer.onViewShown();
		} else {
			this.initializedAsync.then(() => this.onViewShown());
		}
	}

	onViewHidden(closed?: boolean): void {
		if (this.initialized) {
			if (closed) {
				const injectClose = this.reg?.getList<(app: WspApp) => void>("wspApp:close");
				if (injectClose) for (const lstn of injectClose) lstn(this);
				this.reg?.env.place?.closePlace();
			}
			if (this.workbench) VIEWS.onContainerHidden(this.workbench, closed);
			this.itemViewer?.onViewHidden(closed);
			this._lastMigrPopup?.close();
			if (closed) this.reg?.close();
		} else {
			this.initializedAsync.then(() => this.onViewHidden(closed));
		}
	}

	updateAppDef(def: JWspAppDef): boolean {
		if (this.appDef === def) return true; //chgt issu d'ici
		//Si changement d'atelier on remplace totalement cette app.
		if (this.appDef.wsp && def.wsp !== this.appDef.wsp) return false;
		this.appDef = def;
		if (this.initialized) {
			if (this.wsp && this.wsp.isAvailable) {
				this.onAppDefChange();
			} else {
				this.reloadApp();
			}
		} else {
			this.initializedAsync.then(() => this.onAppDefChange());
		}
		return true;
	}

	protected onAppDefChange() {
		if (this.appDef.srcRef) this.infoBroker.dispatchInfo(new InfoFocusItem(this.appDef.srcRef), this);
	}

	onInfo(info: IInfo): void {
		if (this.initialized) {
			if (info instanceof InfoCurrentItem) {
				const shortDesc = info.shortDesc;
				const srcRef = shortDesc ? SRC.srcRef(shortDesc) : null;
				if (srcRef != this.appDef.srcRef) {
					const revertable = this.appDef.srcRef != null; //on ne sait pas reculer d'un srcRef existant vers aucun srcRef sélectionné.
					this.appDef.srcRef = srcRef;
					this.dispatchEvent(new CustomEvent('c-appdef-change', {bubbles: true, detail: {revertable}}));
				}
				this._refreshTitle(shortDesc ? ITEM.getSrcMainName(this.reg, shortDesc, this.wsp.wspMetaUi) : null);
			}
		} else {
			this.initializedAsync.then(() => this.onInfo(info));
		}
	}

	buildLastDatas(parentLastDatas: JLastDatas): void {
		LASTDATAS.buildLastDatas(parentLastDatas, this.workbench, true);
	}

	/** Ouverture en corbeille des items supprimés. */
	protected async fallbackOpenSrcRef(this: IItemViewer, srcRef: srcRef, longdesc: JSrcFields, silent?: boolean): Promise<true | null> {
		if (longdesc.srcSt < 0) {
			OpenWspTrash.openTrashDialog(this.reg.env.uiRoot, {reg: this.reg, showMainView: true, selectLiveSrcUri: longdesc.srcLiveUri || longdesc.srcUri});
			return true;
		}
		return null;
	}

	/** Init des extPoints du registre de l'app. */
	protected async initRegExtPoints() {
		const app = this;
		const reg = this.reg;
		const wsp = reg.env.wsp;

		(reg.env as IHistoEditEnv).histoEditMgr = new HistoEditPointsMgr();

		//#### Actions transversales sur les IShortDescCtx.
		function addShortDescAction(action: IAction<IShortDescCtx>, accelKey?: string, sortKey?: number) {
			reg.addToList("actions:wsp:shortDesc", action.getId(), 1, action, sortKey);
			if (accelKey) reg.addToList("accelkeys:wsp:shortDesc", accelKey, 1, action);
		}

		addShortDescAction(new FocusItemOrSpace(), "F3");
		addShortDescAction(ShortDescCopy.SINGLETON, "c-accel");//sortKey -1 ?
		addShortDescAction(new RenameSrc(), "F2");
		addShortDescAction(new DeleteSrc());
		addShortDescAction(new ExportScar());
		addShortDescAction(new ShowNetItems(), "e-accel-shift");

		this.addWspActionsMain("actions:wspApp:main", "actions:wspApp");
		this.addGlobalAction("actions:wspApp:main", new WorkbenchTools(), 60);
		this.addGlobalAction("actions:wspApp:main", new OpenWspDocApp(), 70, "F8");

		//Config
		this.addWspActionsConfig("actions:wspApp:config");

		//Content
		this.addWspActionsContent("actions:wspApp:content");

		//Layers
		this.addWspActionsLayers("actions:wspApp:layers");

		this.addGlobalAction("actions:wspApp:more", new OpenFastFindItem(), 10, "<-accel");
		this.addGlobalAction("actions:wspApp:more", new OpenSearchItems(), 20, "f-accel+shift", true);

		//#### ScProtocol
		registerWspProtocolsActions(reg);

		//#### Views du workbench
		reg.addToList("wspApp:tools:lt", "explorer", 1, new ExplorerArea()
				.setHideItemFolderContent(true)
				.setActionsLists(["actions:wspApp:explorer", "actions:wsp:shortDesc"])
				.setAccelKeysLists(["accelkeys:wspApp:explorer", "accelkeys:wsp:shortDesc"])
			, 1);

		reg.addToList("wspApp:tools:bl", "gen", 1, new GeneratorTabsArea(), 1);

		reg.addToList("wspApp:tools:br", "itemSearches", 1, new SearchesArea('itemSearches', 'item')
				.setActionsLists(["actions:wspApp:shortDesc:search:item", "actions:wsp:shortDesc"])
				.setAccelKeysLists(["accelkeys:wspApp:shortDesc:search:item", "accelkeys:wsp:shortDesc"])
			, 2);

		//Fournisseur de requête par défaut.
		reg.registerSvc('searchStore.item', 1, {
			async persistSearch(search: ISearchPersist): Promise<void> {
				//console.log("persistSearch:::::", search);
				const view = await app.workbench.showView('itemSearches') as Searches;
				if (view) return view.persistSearch(search);
			}
		} as ISearchStore);

		reg.registerSvc('searchStore.task', 1, {
			async persistSearch(search: ISearchPersist): Promise<void> {
				//console.log("persistSearch:::::", search);
				const view = await app.workbench.showView('taskSearches') as Searches;
				if (view) return view.persistSearch(search);
			}
		} as ISearchStore);

		reg.addToList("wspApp:tools:rt", "bookmarks", 1, new BookmarksArea()
				.setActionsLists(["actions:wspApp:bookmarks", "actions:wsp:shortDesc"])
				.setAccelKeysLists(["accelkeys:wspApp:bookmarks", "accelkeys:wsp:shortDesc"])
			, 1);

		reg.addToList("wspApp:tools:lb", "toc", 1, new TocArea(), 1);
		reg.addToList("wspApp:tools:lb", "netItems", 1, new NetItemsArea()
				.setConfsList(["netItems:confs"])
				.setActionsLists(["actions:wspApp:netItems", "actions:wsp:shortDesc"])
				.setAccelKeysLists(["accelkeys:wspApp:netItems", "accelkeys:wsp:shortDesc"])
			, 1);

		//#### Actions du SpaceTree
		// TODO PERF isoler la déclaration des actions du SpaceTree en dépendance async de ExplorerArea...
		// (si SpaceTree ne devient plus systématiquement utilisé / majeur dans un workbench d'un wsp)

		function addSpaceTreeAction(action: IAction<IShortDescCtx>, accel?: string, sortKey?: number) {
			reg.addToList("actions:wspApp:explorer", action.getId(), 1, action, sortKey);
			if (accel) reg.addToList("accelkeys:wspApp:explorer", accel, 1, action);
		}

		addSpaceTreeAction(new SpaceTreeFocusItem(), "F3");
		addSpaceTreeAction(new SpaceTreeCollapseAllAction());

		const pasteCtrl = new SpaceTreePaste();
		addSpaceTreeAction(pasteCtrl, "v-accel");
		addSpaceTreeAction(new SpaceTreePasteMove(pasteCtrl));

		addSpaceTreeAction(new SpaceTreeCreateItem(), "i-accel");
		addSpaceTreeAction(new SpaceTreeCreateSpace());
		addSpaceTreeAction(new SpaceTreeRename(), "F2");
		addSpaceTreeAction(new SpaceTreeMoveTo());
		addSpaceTreeAction(new DeleteSrc(), "Delete");

		addSpaceTreeAction(new OpenSearchItems(), "f-accel-shift");

		addSpaceTreeAction(new SpaceTreeDuplicate());
		addSpaceTreeAction(new SpaceTreeCopyTo());

		addSpaceTreeAction(new SetPermsSpace().requireSrcVisiblePerm(["ui.apps.set.perms.space", "admin.node.configRoles.byAdmin"]));

		addSpaceTreeAction(ImportRes.SINGLETON());
		addSpaceTreeAction(ImportFolder.SINGLETON());
		addSpaceTreeAction(new ImportFiles());
		addSpaceTreeAction(new ImportScar());

		addSpaceTreeAction(ExportRes.SINGLETON());
		//addSpaceTreeAction(new ExportScar()); //ajouté dans addShortDescAction
		addSpaceTreeAction(new ExportScwsp());

		//#### Colonnes des taches
		function addColTask(colDef: GridColDef, sortKey: number, registerCodefAsSvcCode?: string) {
			if (registerCodefAsSvcCode) {
				reg.registerSvc(registerCodefAsSvcCode, 1, colDef);
				reg.addSvcToList("columns:wspApp:searches:task", colDef.id, 1, registerCodefAsSvcCode, sortKey);
			} else {
				reg.addToList("columns:wspApp:searches:task", colDef.id, 1, colDef, sortKey);
			}
		}

		function addColTaskExport(colDef: SearchExportColumn<IRegPointer<IWspUiEnv>>, sortKey?: number) {
			reg.addToList("columns:wspApp:searches-export:task", colDef.getId(), 1, colDef, sortKey);
		}

		//#### Colonnes des items
		function addCol(colDef: GridColDef, sortKey: number) {
			reg.addToList("columns:wspApp:searches:item", colDef.id, 1, colDef, sortKey);
		}

		function addColExport(colDef: SearchExportColumn<IRegPointer<IWspUiEnv>>, sortKey?: number) {
			reg.addToList("columns:wspApp:searches-export:item", colDef.getId(), 1, colDef, sortKey);
		}

		// srcCode
		addCol(new GridColDef('srcCode').setLabel("Item").setSortable(true)
			.setFlex('20%', 1, 1).setMinWidth('5em')
			.setCellBuilder(new CellBuilderSrcIconCode(this.reg, this.wsp.wspMetaUi, true, this.wsp.srcUriItemsSortFn)), 0);
		addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('srcCode').setColDef(new SearchFuncUri2LeafName("srcUri")).setLabel("Code"), 0);

		addCol(new GridColDef('title').setMinWidth('3em')
			.setLabel("Titre").setSortable(true)
			.setFlex('30%', 2, 2)
			.setCellBuilder(new CellBuilderString('itTi')), 10);
		addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('title').setColDef(new SearchFuncProps("itTi")).setLabel("Titre"), 30);

		addCol(new GridColDef('type').setMinWidth('3em')
			.setLabel("Type d'item").setSortable(true)
			.setFlex('20%', 2, 2)
			.setCellBuilder(new CellBuilderItModel(this.reg, this.wsp.wspMetaUi)), 20);
		addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('type').setColDef(
			new SearchFuncTranslateStr("itModel").setDict(() => {
				const it = this.wsp.wspMetaUi.getItemTypes();
				const dict: { [key: string]: string } = {};
				it.forEach(entry => {if (!entry.isFamily(EItemTypeFamily.task)) dict[entry.getModel()] = entry.getTitle()});
				return dict;
			})
		).setExportStatus(ESearchExportColumnStatus.exportable).setLabel("Type d'item"));

		// space
		addCol(new GridColDef('space')
			.setLabel("Espace").setSortable(true)
			.setFlex('25%', 2, 2).setMinWidth('2em')
			.setCellBuilder(new CellBuilderSpace()), 20);
		addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('space').setColDef(new SearchFuncUri2Space("srcUri")).setLabel("Espace"), 20);

		// date
		addCol(new GridColDef('dt')
			.setLabel("Modif.").setDescription("Date de dernière modification").setSortable(true)
			.setFlex('6em', 1, 1).setMinWidth('2em')
			.setCellBuilder(new CellBuilderDate('srcDt')), 30);
		addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('dt').setColDef(new SearchFuncLong2Date("srcDt")).setLabel("Date modification").setDescription("Date de la dernière modification").setExportStatus(ESearchExportColumnStatus.exportable));

		if (reg.env.universe.config.backEnd !== 'fs') {
			addCol(new GridColDef('by')
				.setLabel("Par").setSortable(true).setMinWidth('2em')
				.setFlex('6em', 1, 1)
				.setCellBuilder(new CellBuilderAccount(this.reg, 'srcUser')), 31);
			addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('by').setColDef(
				new SearchFuncAccountProp("srcUser")
			).setLabel("Dernier contributeur").setExportStatus(ESearchExportColumnStatus.exportable));

		}

		const wspDefProps = wsp.wspDefProps;
		if (wspDefProps.drfRefWsp) (await import("back/wsp/areas/drfOverrides.js")).initDrfReg(reg);
		if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
		if (wspDefProps.drvAxis) (await import("back/wsp/areas/drvOverrides.js")).initDrvReg(reg);
		if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond

		if (wsp.hasFeature("git")) {
			const gitSt = (wsp.infoWsp.srcFields as JGitSrcFields).gitSt;
			if (gitSt != null && gitSt !== (-1 as EGitState.none)) {
				(await import("back/wsp/areas/gitOverrides.js")).initGitReg(reg);
				if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
			}
		}

		if (wsp.hasFeature("liveCycle") && wsp.wspMetaUi.getLcTransitions() !== null) {
			reg.addToList("ribbon:item", "lc", 1, RibbonLc, 20);

			const listTr = new (await import("back/wsp/actions/lcTaskActions.js")).ItemListTransition() as ItemListTransition<IShortDescCtx>;
			if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
			const lcName = this.wsp.wspMetaUi.getLcName();
			addShortDescAction(listTr, null, -10);

			addCol(new GridColDef("lc")
					.setFlex('25%', 2, 2).setMinWidth('2em')
					.setCellBuilder(new CellBuilderLc(this.wsp.wspMetaUi))
					.setLabel(lcName),
				15
			);
			addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('lcSt').setColDef(
				new SearchFuncTranslateStr("lcSt").setDict(() => {
					const map: { [key: string]: string } = {};
					const lcStates = this.wsp.wspMetaUi.getLcStates();
					for (const st of lcStates.values()) {
						map[st.code] = st.name;
					}
					return map;
				})
			).setLabel(lcName).setExportStatus(ESearchExportColumnStatus.exportable));
			addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('lcDt').setColDef(
				new SearchFuncLong2Date("lcDt")
			).setLabel(`${lcName} - date d'affectation`).setDescription(`${lcName} : date de la dernière modification`).setExportStatus(ESearchExportColumnStatus.exportable));
			addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('lcBy').setColDef(
				new SearchFuncAccountProp("lcBy")
			).setLabel(`${lcName} - affecté par`).setExportStatus(ESearchExportColumnStatus.exportable));


			const gridColLc = new GridColDef("lc")
				.setFlex("1rem", 0, 0)
				.setCellBuilder(new CellBuilderLcFlag(this.wsp.wspMetaUi).setIconClass("lcState"))
				.setSkin("wsp-explorer/grid/lc")
				.setLabel(lcName);
			reg.addToList('columns:wspExplorer:secondaryCols', 'lc', 1, gridColLc, 5);
			reg.addToList('columns:netItems:secondaryCols', 'lc', 1, gridColLc, 5);

			reg.registerSkin("wsp-explorer/grid/lc", 1, /* language=CSS */ `
		  .lcState {
			  background-repeat: no-repeat;
			  background-position: center;
			  background-size: contain;
			  filter: var(--filter);
		  }

		  .lc {
			  border: none !important;
		  }
			`);

		}

		if (wsp.hasFeature("resp") && wsp.wspMetaUi.hasResps) {
			// - Items
			const listRespsItems = wsp.wspMetaUi.listResps('item');
			if (listRespsItems) {
				listRespsItems.forEach((entry) => {
					addCol(new GridColDef('resp.' + entry.code)
						.setLabel(entry.name)
						.setDescription(entry.desc)
						.setSortable(true)
						.setMinWidth('4em')
						.setFlex('2em', 1, 1)
						.setCellBuilder(
							entry.userCard === 'single' ?
								new CellBuilderResp(entry.code, reg, 'rspUsrs', false) :
								new CellBuilderResps(entry.code, reg, 'rspUsrs', false)
						), 100);

					addColExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('resp.' + entry.code).setColDef(
						new SearchFuncAccountProp(new SearchFuncResponsibles(entry.code))
					).setLabel(entry.name).setExportStatus(ESearchExportColumnStatus.exportable));

				})
			}
			reg.addToList("ribbon:item", "resp", 1, RibbonResp, 20);
			const setResp = new (await import("back/wsp/actions/itemActions.js")).ItemsSetResonsabilities() as ItemsSetResonsabilities<IShortDescCtx>;
			if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
			addSpaceTreeAction(setResp, null, -10);
			reg.addToList("actions:item:burger", setResp.getId(), 1, setResp, -10);
			// - Tasks
			if (wsp.hasFeature("tasks")) {
				const listRespsTasks = wsp.wspMetaUi.listResps('task');
				if (listRespsTasks) {
					listRespsTasks.forEach((entry) => {
						addColTask(new GridColDef(`resp.${entry.code}`)
								.setLabel(entry.name)
								.setDescription(entry.desc)
								.setSortable(true)
								.setMinWidth('4em')
								.setFlex('2em', 1, 1)
								.setCellBuilder(
									entry.userCard === 'single' ?
										new CellBuilderResp(entry.code, reg, 'rspUsrs', false) :
										new CellBuilderResps(entry.code, reg, 'rspUsrs', false)
								),
							100,
							`wsp-columns-resp-${entry.code}`);
						addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('resp.' + entry.code).setColDef(
							new SearchFuncAccountProp(new SearchFuncResponsibles(entry.code))
						).setLabel(entry.name).setExportStatus(ESearchExportColumnStatus.exportable));
					});
				}

			}
		}

		if (wsp.hasFeature("tasks") && wsp.wspMetaUi.hasTasks) {
			await import("back/wsp/actions/taskActions.js");
			if (this._lastWspMetaUi !== this.wsp.wspMetaUi) return; //raceCond
			const tasksIt: ItemTypeTask[] = wsp.wspMetaUi.getItemTypes().filter((entry) => entry.isFamily(EItemTypeFamily.task)) as ItemTypeTask[];
			reg.addToList("wspApp:tools:rb", "tasks", 1, new TasksArea(), 1);
			reg.addToList("ribbon:item", "tasks", 1, RibbonTasks, 30);

			reg.addToList("wspApp:tools:br", "taskSearches", 1, new SearchesArea('taskSearches', 'task')
					.setActionsLists(["actions:wspApp:shortDesc:search:task", "actions:wsp:shortDesc:task"])
					.setAccelKeysLists(["accelkeys:wspApp:shortDesc:search:task", "accelkeys:wsp:shortDesc:task"])
				, 1);

			// - itTi
			addColTask(new GridColDef('itTi')
				.setLabel("Libellé").setSortable(true)
				.setFlex('6em', 1, 1).setMinWidth('3em')
				.setCellBuilder(new CellBuilderString('itTi').override('fillSearchColumns', (map: Map<string, string | ISearchFunc>) => {
					map.set('itTi', null);
					map.set('actStage', null);// besoin pour mise en forme conditionnelle
				})), 30);
			addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('title').setColDef(new SearchFuncProps("itTi")).setLabel("Libellé"), 30);

			// - deadline
			if (tasksIt.find(entry => entry.hasFeature(ETaskFeatures.deadline))) {
				addColTask(new GridColDef('deadline')
					.setLabel("Date limite").setSortable(true)
					.setFlex('2em', 1, 1).setMinWidth('2em')
					.setCellBuilder(new CellBuilderTkDeadline()), 30);
				addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('deadline').setColDef(
					new SearchFuncLong2Date("tkDeadline").setFormat(ELong2DateStyles.short, null)
				).setLabel("Date limite"), 100);
			}

			// - scheduling
			if (tasksIt.find(entry => entry.hasFeature(ETaskFeatures.scheduling))) {
				addColTask(new GridColDef('scheduling')
					.setLabel("Planification").setDescription("Date de planification").setSortable(true)
					.setFlex('2em', 1, 1).setMinWidth('2em')
					.setCellBuilder(new CellBuilderTkScheduledD()), 40);
				addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('scheduling').setColDef(
					new SearchFuncLong2Date("tkScheduledDt").setFormat(ELong2DateStyles.short, null)
				).setLabel("Planification"), 100);
			}

			// -completedDt
			addColTask(new GridColDef('completedDt')
				.setLabel("Date de clôture").setSortable(true)
				.setFlex('2em', 1, 1).setMinWidth('2em')
				.setCellBuilder(new CellBuilderDate('tkCompletedDt')), 200);
			addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('completedDt').setColDef(
				new SearchFuncLong2Date("tkCompletedDt").setFormat(ELong2DateStyles.short, null)
			).setLabel("Date de clôture"), 110);

			// date modif
			addColTask(new GridColDef('dt')
				.setLabel("Modif.").setDescription("Date de dernière modification").setSortable(true)
				.setFlex('6em', 1, 1).setMinWidth('2em')
				.setCellBuilder(new CellBuilderDate('srcDt')), 300);
			addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('dt').setColDef(
				new SearchFuncLong2Date("srcDt")
			).setLabel("Date modification").setDescription("Date de la dernière modification").setExportStatus(ESearchExportColumnStatus.exportable));

			// - type
			/**addColTask(new GridColDef('type').setMinWidth('3em')
				.setLabel("Type").setSortable(true)
				.setFlex('3em', 2, 2)
				.setCellBuilder(new CellBuilderItModel(this.reg, this.wsp.wspMetaUi)), 0);**/
			addColTask(new GridColDef('type').setLabel("")
					.setMinWidth('1.5em').setMaxWidth('1.5em').setSortable(true)
					.setCellBuilder(new CellBuilderItModelFlagIcon(this.reg, this.wsp.wspMetaUi))
				, 0);
			addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('type').setColDef(
				new SearchFuncTranslateStr("itModel").setDict(() => {
					const dict: { [key: string]: string } = {};
					tasksIt.forEach(entry => dict[entry.getModel()] = entry.getTitle());
					return dict;
				})
			).setExportStatus(ESearchExportColumnStatus.exportable).setLabel("Type"));

			// - LC (un LC potentiellement par type de task)
			if (tasksIt.find(entry => entry.getLcStates()?.size)) {
				const lcName = LC_DEFAULT_NAME;
				addColTask(new GridColDef("lcSt")
						.setFlex('3em', 2, 2)
						.setCellBuilder(new CellBuilderLc(this.wsp.wspMetaUi))
						.setLabel(lcName),
					50);
				// Par approximation, on estime que pour un code donné, le label sera unique quelque soit le lcTask
				// FIXME : coder une SearchFunc dédiée pour améliorer celà
				const lcStates: Map<string, string> = new Map();
				tasksIt.forEach((entry) => {
					if (entry.getLcName()) {
						const states = entry.getLcStates();
						states?.forEach(entry => lcStates.set(entry.code, entry.name));
					}
				});
				addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('lcSt-unified').setColDef(
					new SearchFuncTranslateStr("lcSt").setDict(() => {
						const map: { [key: string]: string } = {};
						lcStates.forEach((value, key) => map[key] = value);
						return map;
					})
				).setLabel(lcName).setExportStatus(ESearchExportColumnStatus.exportable));

				addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('lcDt').setColDef(
					new SearchFuncLong2Date("lcDt")
				).setLabel(`${lcName} - date d'affectation`).setDescription(`${lcName} : date de la dernière modification`).setExportStatus(ESearchExportColumnStatus.exportable));
				addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('lcBy').setColDef(
					new SearchFuncAccountProp("lcBy")
				).setLabel(`${lcName} - affecté par`).setExportStatus(ESearchExportColumnStatus.exportable));

			}

			/*
			// - LC : une col par LC (associé aux **tasks**)
			it.forEach((entry: ItemTypeTask) => {
				if (entry.isFamily(EItemTypeFamily.task) && entry.getLcName()) {
					const entryLc = entry.getLcStates();
					if (entryLc && entryLc.size) {
						addColTaskExport(new SearchExportColumn<IRegPointer<IWspUiEnv>>('lcSt-' + entry.getModel()).setColDef(
					new SearchFuncTranslateStr("lcSt").setDict(() => {
								const map: { [key: string]: string } = {};
								for (const st of entryLc.values()) {
									map[st.code] = st.name;
								}
								return map;
							})
				).setLabel(entry.getLcName()).setExportStatus(ESearchExportColumnStatus.exportable));
					}
				}
			});
			*/

			function addShortDescTaskAction(action: IAction<IShortDescCtx> | string, accelKey?: string) {
				if (typeof action === 'object') {
					reg.addToList("actions:wsp:shortDesc:task", action.getId(), 1, action);
					if (accelKey) reg.addToList("accelkeys:wsp:shortDesc:task", accelKey, 1, action);
				} else {
					reg.addSvcToList("actions:wsp:shortDesc:task", action, 1, action);
					if (accelKey) reg.addSvcToList("accelkeys:wsp:shortDesc:task", accelKey, 1, action);
				}
			}

			addShortDescTaskAction("tasks/focusTaskAction");
			addShortDescTaskAction(ShortDescCopy.SINGLETON, "c-accel");
			addShortDescTaskAction("tasks/deleteTaskAction", "Delete");
		}
	}
}

REG.reg.registerSkin('wsp-app', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		padding-top: 2px;
	}

	header {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		max-height: 5em;
		overflow: hidden;
		align-items: center;
		justify-content: center;
		user-select: none;
	}

	img {
		height: 1.3em;
	}

	h1 {
		margin: .1em .5em;
		font-size: 1em;
		letter-spacing: 0.1em;
		text-align: center;
	}

	hr {
		display: none;
	}

	main {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
	}
`);

REG.reg.registerSkin('wsp-app/c-workbench', 1, /* language=CSS */ ``);

customElements.define('wsp-app', WspApp);


class WorkbenchTools extends ActionMenu<WspApp> {

	constructor() {
		super("tools");
		this._label = "Volets";
		this._description = "Affichage des volets latéraux";
		//this._icon = "/@skin@/wsp/apps/wspApp/views.svg"
	}

	getActions(ctx: WspApp): IAction<WspApp>[] {
		if (!this._actions) {
			this._actions = ctx.workbench.getAreaTools().map((a) => new ShowTool(a));
			this._actions.push(new ActionSeparator());
			this._actions.push(new ShowFlapTabs());
		}
		return this._actions as IAction<WspApp>[];
	}
}

class ShowTool extends ActionSignboardWrapper<WspApp> {
	constructor(toolArea: IArea<WspApp>) {
		super(toolArea.getId());
		this._wrapped = toolArea;
	}

	isToggle(ctx: WspApp): this is IActionToggle<WspApp> {return true}

	getDatas(api: "toggle", ctx: WspApp): any {return ctx.workbench.getTab(this._wrapped.getId())?.selected || false}

	execute(ctx: WspApp, ev?: Event): any | "noPreventDefault" {
		ctx.workbench.toggleAreaTool(this._wrapped.getId());
	}
}

class ShowFlapTabs extends Action<WspApp> implements IActionToggle<WspApp> {
	constructor() {
		super("showFlapTabs");
		this._label = "Afficher les onglets";
	}

	isToggle(ctx: WspApp): this is IActionToggle<WspApp> {return true}

	getDatas(api: "toggle", ctx: WspApp): any {
		return !ctx.workbench.classList.contains("hideFlapTabs");
	}

	execute(ctx: WspApp, ev?: Event): any | "noPreventDefault" {
		const showFlapTabs = !ctx.workbench.classList.toggle("hideFlapTabs");
		const persistUS = ctx.reg.getPersistUserStates();
		if (persistUS) persistUS.setUserDatas({showFlapTabs});
	}
}


class OpenWspDocApp extends Action<WspApp> {
	constructor() {
		super('openWspDoc');
		this._label = "Mode plan";
		this._icon = "/@skin@/wsp/views/toc/toc.svg";
		this._group = "other";
		this._visPerms = "ui.wspDocApp";
	}

	isVisible(ctx: WspApp): boolean {
		//XXX à remplacer en 5.1 par un deny sur role ~default de la perm ui.wspDocApp avec levelAuth max ?
		if (!ctx.reg.getPref<boolean>("wspDocApp.enabled", false)) return false;
		return super.isVisible(ctx);
	}

	execute(ctx: WspApp, ev?: Event): any | "noPreventDefault" {
		const curr = new InfoReqCurrentItem();
		ctx.infoBroker.dispatchInfo(curr, ctx);
		if (curr.shortDesc && ctx.reg.env.wsp.wspMetaUi.getItemType(curr.shortDesc.itModel).datas.asDoc) {
			(desk as AppFrameDeskFeat).findAndOpenApp({u: ctx.universe.getId(), wspDoc: ctx.wsp.code, doc: SRC.srcRef(curr.shortDesc)} as JWspDocAppDef, ev);
		} else {
			(desk as AppFrameDeskFeat).findAndOpenApp({u: ctx.universe.getId(), wspDoc: ctx.wsp.code} as JWspDocAppDef, ev);
		}
	}
}

